import { Overlay } from '../ui/Overlay';

/* ---------------------------------------------------------------------------
   More — the fifth slot in the bottom bar (card yOcdjnR6pXBz).

   Jonathan 2026-10-11 (card 2u2srEOxUyrW): the More menu should slide in from
   the right like the JitsLab drawer primitive, the same way Settings does
   (card TbWv84mN6k2Q) — not pop up as a bottom sheet.

   Jonathan 2026-10-19 (msg_45e07567, IA v2): the drawer is re-anchored into
   three sections — Us, Life, Notebook — with SEVEN primary items, down from
   a menu that mirrored every sub-tab. The Thread group is gone: the header's
   own filter chips (All | Chat | Asks | Questions) already are the segmented
   control the plan calls for, and a second door to the same filters was
   noise. Consolidations at this layer: Today's question + Game round become
   one "Daily prompts" door (the Us screen's sub-tabs are the internal
   tabs), Dates + Date preferences become "Dates & ideas" (Preferences is a
   segment in the Dates panel's own row, card B9pPdlK1CciE), and Notebook's seven collection links
   collapse to one "Shared notes & lists" door. Deeper screen merges — the
   Action Center triage view, a Daily Prompts hub — are follow-up cards.

   A11y: overlay tap + Escape close, role=dialog, focus moves in on open and
   back out on close, Tab is trapped.
   PORTAL, not inline — the chat screen's .fade-in ends on a transform, which
   makes #chat-screen the containing block for position:fixed: rendered
   inside, an inset:0 overlay resolves against the PANEL's box, not the
   window, and taps fall through to the content behind.
   --------------------------------------------------------------------------- */

/** What a quick link in the sheet asks the shell to do. */
export interface MorePick {
  /** Main nav item (Thread / Us / Life / Notebook / Search). */
  tab?: 'thread' | 'us' | 'life' | 'notebook' | 'search';
  /** Thread sub-view: land on that filter. No drawer row offers these any
   *  more (IA v2) — the Thread header chips own that job — but the shell
   *  seam stays for deep links. */
  threadFilter?: 'all' | 'chat';
  /** Land on the tab, then scroll this section into view. */
  scrollTo?: string;
  /** Notebook sub-view: land on that collection tab. */
  collection?: string;
  /** Life / Us sub-tab to open before scrolling (card sjFxIbWi3UgN) — the
   *  target section only exists while its tab is open. */
  lifeSub?: string;
  usSub?: string;
  /** Open the Dates preferences segment on arrival (card GMat5z0Zxutf:
   *  the gear is dual-linked from here, not only inline in the Dates
   *  panel). Only meaningful with usSub 'dates'. */
  usPrefs?: boolean;
  /** Settings lives in this menu now, not in the header. */
  settings?: boolean;
  /** The concierge's walk-through — a screen, not a destination (Jonathan 2026-10-07). */
  tour?: boolean;
  /** A screen over the tabs (card YMMXiFOdFgdF) — not a tab, opened like Settings.
   *  Reveal history and routing rules moved into Us, Questions and Life, Inbox
   *  (cards IlGn5h3p3wDk, oQXsyU5lUDtV); Help lives in Settings → About. Vault
   *  is a destination of its own (card t193KwD1i7aY) — a private room, so it
   *  gets a door here rather than a tab. Search left this seam when it was
   *  promoted into the tab rail (card 2Cium84y07TE): it is a tab now, so its
   *  row asks for `tab: 'search'`. */
  view?: 'help';
}

interface MoreGroup {
  tab: NonNullable<MorePick['tab']>;
  label: string;
  subs: Array<{ label: string; pick: MorePick }>;
}

/**
 * IA v3 (Jonathan 2026-10-20): one door per real path, not per euphemism.
 * Us lists the screens it actually has — Questions, Game, Dates, Digest —
 * with the two date surfaces consolidated under "Date Plans" (the prefs
 * gear is dual-linked inline in the Dates panel, card GMat5z0Zxutf).
 * Notebook keeps its seven collections instead of one collapsed door.
 * Life's lead door is "For review", not "Action center".
 */
const GROUPS: MoreGroup[] = [
  {
    tab: 'us',
    label: 'Us',
    subs: [
      { label: "Today's question", pick: { tab: 'us', usSub: 'questions', scrollTo: 'us-lane' } },
      { label: 'Games', pick: { tab: 'us', usSub: 'game', scrollTo: 'game-panel' } },
      // Dates + Date preferences consolidated: Preferences is one of the Dates
      // panel's segments (card B9pPdlK1CciE), so one door suffices.
      { label: 'Date Plans', pick: { tab: 'us', usSub: 'dates', scrollTo: 'us-dates-panel' } },
      { label: 'Partner digest', pick: { tab: 'us', usSub: 'digest', scrollTo: 'digest-panel' } },
    ],
  },
  {
    tab: 'life',
    label: 'Life',
    subs: [
      // Lead door for the dashboard. Labelled "Today" — what needs you and
      // what is coming up, merged across the Life surfaces.
      { label: 'Today', pick: { tab: 'life', lifeSub: 'today', scrollTo: 'life-today-panel' } },
      // The triage work (the "needs you" lane). Labelled "For review"
      // (Jonathan 2026-10-20) — the work waiting on a reply.
      { label: 'For review', pick: { tab: 'life', lifeSub: 'requests', scrollTo: 'life-lane' } },
      { label: 'Household', pick: { tab: 'life', lifeSub: 'chores', scrollTo: 'life-chores-panel' } },
      { label: 'Weekly sync', pick: { tab: 'life', lifeSub: 'plan', scrollTo: 'life-plan-panel' } },
      // Off the sub-tab row since Today became the dashboard; still one door
      // away, and #life-admin still lands on it.
      { label: 'Inbox', pick: { tab: 'life', lifeSub: 'admin', scrollTo: 'life-admin-panel' } },
    ],
  },
  {
    tab: 'notebook',
    /* The row label must match the destination's own title, so the group reads
       Notebook (Jonathan 2026-10-28: the surface keeps its name). */
    label: 'Notebook',
    subs: [
      // Detail kept (Jonathan 2026-10-20): one row per collection, matching
      // the screen's own tabs, rather than one collapsed "everything" door.
      { label: 'Everything', pick: { tab: 'notebook', collection: 'all' } },
      // "Links" is called COLLECTION now (founder email 2026-10-21); same filter.
      { label: 'Collection', pick: { tab: 'notebook', collection: 'link' } },
      { label: 'Gifts', pick: { tab: 'notebook', collection: 'gift' } },
      { label: 'Memories', pick: { tab: 'notebook', collection: 'memory' } },
      { label: 'Date ideas', pick: { tab: 'notebook', collection: 'date' } },
      { label: 'Notes', pick: { tab: 'notebook', collection: 'note' } },
      { label: 'Ideas', pick: { tab: 'notebook', collection: 'idea' } },
    ],
  },
];

interface MoreMenuProps {
  open: boolean;
  onClose: () => void;
  onPick: (pick: MorePick) => void;
  /** Jonathan 2026-10-16 (msg_6b9579f2): Sign out lives in THIS menu, not
   *  only inside Settings. Same handler the Settings row uses. */
  onSignOut?: () => void;
}

/**
 * A11y now comes from the shared Overlay primitive (UX item 6): focus in and
 * back out, Tab trapped, Escape closes, the app behind is inert. PORTAL, not
 * inline — the chat screen's .fade-in ends on a transform, which makes
 * #chat-screen the containing block for position:fixed: rendered inside, an
 * inset:0 overlay resolves against the PANEL's box, not the window, and taps
 * fall through to the content behind.
 */
export function MoreMenu({ open, onClose, onPick, onSignOut }: MoreMenuProps) {
  const take = (pick: MorePick, e?: { currentTarget: HTMLElement }) => {
    // Clear any focus/highlight left on the tapped row before the drawer
    // closes — a Settings/Sign out row otherwise keeps its selected state
    // after use (card z7exIbgNvPst).
    e?.currentTarget?.blur?.();
    onPick(pick);
  };

  return (
    <Overlay
      open={open}
      onClose={onClose}
      className="more-drawer-overlay"
      panelClassName="more-drawer"
      panelId="more-menu"
      label="Explore — every screen and sub-view"
      testId="more-overlay"
      restoreFocusTo="#btn-more"
    >
      <div className="more-head">
        <span className="more-title">Go to</span>
        <button type="button" className="more-close" id="btn-more-close" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="more-body">
        {GROUPS.map(g => (
          <section className="more-group" key={g.tab}>
            <button
              type="button"
              className="more-row more-group-title"
              data-more-tab={g.tab}
              data-more-heading
              onClick={e => take({ tab: g.tab }, e)}
            >
              {g.label}
            </button>
            <div className="more-subs">
              {g.subs.map(s => (
                <button
                  type="button"
                  className="more-sub"
                  key={s.label}
                  onClick={e => take(s.pick, e)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </section>
        ))}

        {/* Reveal history and Inbox routing rules left this menu (cards
            IlGn5h3p3wDk, oQXsyU5lUDtV): each already had its real home — Us,
            Questions and Life, Inbox — and the menu was carrying a duplicate
            door to the same room. */}

        {/* Help and the tour moved into Settings → About (cards Yo089ycejH4O
            and yOszUUb0Nnc2): this menu is the list of DESTINATIONS, and a
            explanation screen and a nine-step walk were two rows of noise
            competing with the places themselves. */}

        {/* Search (card UW7Lu06zMJp9, promoted by 2Cium84y07TE): Jonathan
            2026-10-25 made it a destination of its own; it now sits in the tab
            rail, so this row is a second door to the SAME place — it selects
            the tab rather than opening a panel over it (one destination, one
            behaviour). Kept because the drawer is how the phone reaches
            everything without hunting the rail. */}
        <button
          type="button"
          className="more-row"
          id="btn-search"
          aria-label="Search"
          onClick={e => take({ tab: 'search' }, e)}
        >
          Search
        </button>
        {/* Vault left this menu (card 3YypYdChpTZD, founder email 2026-10-31):
            it is a tab inside Notebook now, where the things you save live —
            a second door in Explore only split the same room in two. */}
        {/* Settings is a row in this menu now — the header gear is gone
            (card yOcdjnR6pXBz: reachable only via More). */}
        <button
          type="button"
          className="more-row more-settings"
          id="btn-settings"
          aria-label="Settings"
          onClick={e => take({ settings: true }, e)}
        >
          Settings
        </button>
        {/* Sign out (Jonathan 2026-10-16): reachable from the menu itself, not
            buried in Settings. The menu closes first so the pair screen it
            lands on isn't behind an open overlay. */}
        <button
          type="button"
          className="more-row more-signout"
          id="btn-more-signout"
          onClick={() => {
            onClose();
            onSignOut?.();
          }}
        >
          Sign out
        </button>
        {/* No LegalFooter here (Jonathan 2026-10-20): the menu is destinations
            and account actions, not a site footer. Privacy / Terms still live
            on the landing and inside Settings. */}
      </div>
    </Overlay>
  );
}
